import type { LatLong } from '@iheartjane/geolocation';
import { Storage } from '@iheartjane/utils';
import type { Dispatch, ReactNode, SetStateAction } from 'react';
import { createContext, useCallback, useContext, useState } from 'react';

/* eslint-disable-next-line no-restricted-imports */
import type {
  Preferences,
  StoreFulfillmentTypes,
  StoreTypes,
} from '@jane/shared/models';
import type { UserLocation } from '@jane/shared/types';

const getInitialLocation = (): UserLocation => ({
  cityState: Storage.get<string>('cityState'),
  coordinates: Storage.get<LatLong>('coordinates'),
  countryCode: Storage.get<string>('countryCode'),
  hasResetLocation: false,
  street: Storage.get<string>('street'),
  zipcode: Storage.get<string>('zipcode'),
});

const getInitialPreferences = (): Preferences => ({
  storeAvailability: Storage.get<'openNow' | undefined>('storeAvailability'),
  storeFulfillmentType: Storage.get<StoreFulfillmentTypes>(
    'storeFulfillmentType'
  ),
  storeSearchRadius: Storage.get<number>('storeSearchRadius'),
  storeType: Storage.get<StoreTypes>('storeType'),
});

interface UserPreferences {
  newLocation?: boolean;
  resetLocation: () => void;
  resetPreferences: () => void;
  setNewLocation: Dispatch<SetStateAction<boolean>>;
  setUserLocation: (location: UserLocation) => void;
  setUserPreferences: (preferences: Preferences) => void;
  userLocation: UserLocation;
  userPreferences: Preferences;
}

export const UserPreferencesContext = createContext<UserPreferences>({
  newLocation: false,
  resetLocation: () => null,
  resetPreferences: () => null,
  setNewLocation: () => null,
  setUserLocation: () => null,
  setUserPreferences: () => null,
  userLocation: getInitialLocation(),
  userPreferences: getInitialPreferences(),
});
UserPreferencesContext.displayName = 'UserPreferences';

export const useUserPreferences = () => useContext(UserPreferencesContext);

interface Props {
  children: ReactNode;
}

export const UserPreferencesProvider = ({ children }: Props) => {
  const [userLocation, setUserLocation] = useState(getInitialLocation());
  const [userPreferences, setUserPreferences] = useState(
    getInitialPreferences()
  );
  const [newLocation, setNewLocation] = useState(false);

  const handleSetPreferences = useCallback((preferences: Preferences) => {
    Object.keys(preferences).forEach((key) =>
      Storage.set(key, preferences[key as keyof Preferences])
    );
    setUserPreferences(preferences);
  }, []);

  const handleSetLocation = useCallback((location: UserLocation) => {
    Object.keys(location).forEach((key) =>
      Storage.set(key, location[key as keyof UserLocation])
    );
    setUserLocation(location);
    setNewLocation(true);
  }, []);

  const handleResetLocation = useCallback(() => {
    const locationKeys = [
      'coordinates',
      'cityState',
      'countryCode',
      'street',
      'zipcode',
    ];
    locationKeys.forEach((key) => {
      Storage.remove(key);
    });

    setUserLocation(
      locationKeys.reduce((prev, curr) => ({ ...prev, [curr]: undefined }), {
        hasResetLocation: true,
      })
    );
  }, []);

  const handleResetPreferences = useCallback(() => {
    const preferencesKeys = [
      'storeAvailability',
      'storeFulfillmentType',
      'storeSearchRadius',
      'storeType',
    ];
    preferencesKeys.forEach((key) => {
      Storage.remove(key);
    });

    setUserPreferences(
      preferencesKeys.reduce(
        (prev, curr) => ({ ...prev, [curr]: undefined }),
        {}
      )
    );
  }, []);

  const value = {
    newLocation,
    resetLocation: handleResetLocation,
    setNewLocation,
    resetPreferences: handleResetPreferences,
    setUserLocation: handleSetLocation,
    setUserPreferences: handleSetPreferences,
    userLocation,
    userPreferences,
  };

  return (
    <UserPreferencesContext.Provider value={value}>
      {children}
    </UserPreferencesContext.Provider>
  );
};
